<template>
  <div :class="$style.container">
    <div :class="$style.paper">
      <!-- 로딩 상태 -->
      <div v-if="loading" :class="$style.loading">
        <div :class="$style.spinner"></div>
        <p>게시글을 불러오는 중...</p>
      </div>

      <!-- 에러 상태 -->
      <div v-else-if="error" :class="$style.error">
        <p>{{ error }}</p>
        <button @click="goBack" :class="$style.backButton">뒤로가기</button>
      </div>

      <!-- 게시글 내용 -->
      <div v-else-if="post" :class="$style.content">
        <!-- 모바일 전용 헤더 -->
        <div :class="$style.detailHeader">
          <button :class="$style.backArrowBtn" @click="goBack" title="뒤로가기">
            ←
          </button>
          <button :class="$style.shareBtn" @click="showShareModal = true" title="공유하기">
            <img src="@/assets/images/ios_share.png" alt="공유" :class="$style.shareIcon" />
          </button>
        </div>

        <!-- 게시글 정보 -->
        <div :class="$style.postInfo">
          <h1 :class="$style.title">{{ post.title }}</h1>
          <div :class="$style.meta">
            <div :class="$style.writerInfo">
              <span :class="$style.writer">{{ post.writer }}</span>
              <span v-if="post.writerAuth === 'admin'" :class="$style.adminEmoji">🐾</span>
            </div>
            <div :class="$style.metaRight">
              <span v-if="!post.isPrivate || userStore.auth === 'admin'" :class="$style.viewCount">👀 {{ post.view || 0
                }}</span>
              <span :class="$style.date">{{ formatDate(post.registeredAt) }}</span>
            </div>
          </div>

          <div :class="$style.categoryRow">
            <div :class="$style.categoryLeft">
              <div v-if="post.isNotice" :class="$style.noticeBadge">📢 공지사항</div>
              <div v-if="!post.isNotice && post.category" :class="$style.category">
                {{ getCategoryName(post.category) }}
              </div>
            </div>
            <div :class="$style.categoryRight">
              <!-- 수정/삭제 버튼 (글쓴이 또는 관리자만) -->
              <button v-if="canEditPost" :class="$style.editButton" @click="editPost">수정</button>
              <button v-if="canEditPost" :class="$style.deleteButton" @click="deletePost">
                삭제
              </button>
            </div>
          </div>
        </div>

        <!-- 곡추천: 유튜브 영상 (VideoList처럼 썸네일 클릭 시 같은 자리에서 임베드 재생, 제목/채널 클릭 → 유튜브 이동) -->
        <div v-if="post.category === 'songRecommend' && post.youtubeLink" :class="$style.songRecommendVideoBlock">
          <template v-if="post.youtubeVideoInfo">
            <div :class="$style.songRecommendVideoCard">
              <div :class="$style.videoThumbnailWrapper">
                <div :class="$style.videoContainer">
                  <div v-if="!songRecommendVideoLoaded" :class="$style.thumbnailContainer"
                    @click="loadSongRecommendVideo">
                    <img v-if="songRecommendThumbUrl" :src="songRecommendThumbUrl" :class="$style.videoThumbnail"
                      alt="영상 썸네일" />
                    <div v-else :class="$style.thumbPlaceholder"></div>
                    <div :class="$style.playButton">▶</div>
                  </div>
                  <iframe v-else-if="songRecommendVideoId"
                    :src="`https://www.youtube-nocookie.com/embed/${songRecommendVideoId}?autoplay=1&mute=0`"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen :class="$style.videoEmbedIframe"></iframe>
                </div>
              </div>
              <div :class="$style.videoInfo">
                <div :class="$style.videoHeader">
                  <a :href="post.youtubeLink" target="_blank" rel="noopener noreferrer" :class="$style.videoAuthorLink">
                    <div :class="$style.videoAuthor">
                      <img v-if="post.youtubeVideoInfo.channelThumbnail" :src="post.youtubeVideoInfo.channelThumbnail"
                        :class="$style.authorAvatarImg" alt="채널 프로필" />
                      <div v-else :class="$style.authorAvatar">
                        {{ (post.youtubeVideoInfo.channel || '?').charAt(0).toUpperCase() }}
                      </div>
                      <div :class="$style.authorInfo">
                        <span :class="$style.authorName">{{ post.youtubeVideoInfo.channel || 'YouTube' }}</span>
                      </div>
                    </div>
                  </a>
                </div>
                <a :href="post.youtubeLink" target="_blank" rel="noopener noreferrer" :class="$style.videoTitleLink">
                  <div :class="$style.videoTitleText">{{ post.youtubeVideoInfo.title || '영상 보기' }}</div>
                </a>
              </div>
            </div>
          </template>
          <a v-else :href="post.youtubeLink" target="_blank" rel="noopener noreferrer"
            :class="$style.songRecommendVideoLinkSimple">
            🎵 유튜브 영상 보기
          </a>
        </div>

        <!-- 게시글 내용 -->
        <div :class="$style.postContent">
          <div :class="$style.contentText" v-html="formatContentWithLinks(post.contents)"></div>

          <!-- 이미지 -->
          <div v-if="post.images && post.images.length > 0"
            :class="[$style.postImages, $style[`imageGrid${post.images.length}`]]">
            <div v-for="(imageUrl, index) in post.images" :key="index" :class="$style.postImageWrapper"
              @click="openImageModal(imageUrl)">
              <img :src="imageUrl" :class="$style.postImage" alt="게시글 이미지" />
            </div>
          </div>
        </div>

        <div v-if="isBandOffPost" :class="$style.bandOffSection">
          <div :class="$style.bandOffHeader">
            <h3>꾹오프 엔트리</h3>
            <p>
              총 참가: {{ bandOffConfig.currentParticipants }}
              <span v-if="bandOffConfig.maxParticipants > 0">/ {{ bandOffConfig.maxParticipants }}</span>
            </p>
          </div>
          <p v-if="bandOffError" :class="$style.bandOffError">{{ bandOffError }}</p>
          <div v-if="bandOffLoading" :class="$style.bandOffLoading">엔트리 현황을 불러오는 중...</div>
          <div v-else :class="$style.bandOffSongList">
            <div v-for="song in bandOffSongs" :key="song.id" :class="$style.bandOffSongItem">
              <div :class="$style.bandOffSongTitle">{{ song.title }}</div>
              <div :class="$style.bandOffPartList">
                <button v-for="part in (song.availableParts || [])" :key="`${song.id}-${part}`" :class="[
                  $style.bandOffPartButton,
                  { [$style.bandOffDisabled]: !canEnterPart(song.id, part) },
                ]" :disabled="!canEnterPart(song.id, part) || bandOffSubmitting" @click="enterBandOff(song, part)">
                  {{ part }} {{ getPartCount(song.id, part) }}/{{ getPartLimit(part) || '∞' }}
                </button>
              </div>
              <div v-if="getMyEntry(song.id)" :class="$style.bandOffMyEntry">
                내 엔트리: {{ getMyEntry(song.id).part }}
                <button :class="$style.bandOffCancelButton" :disabled="bandOffSubmitting"
                  @click="cancelMyEntry(getMyEntry(song.id).id)">
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 좋아요 & 신고 버튼 -->
        <div :class="$style.likeSection">
          <button :class="[$style.likeButton, userHasLiked && $style.liked]" @click="toggleLike">
            {{ userHasLiked ? "💙" : "👍" }}
            {{ userHasLiked ? "추천 취소" : "추천" }}
            {{ post.like }}
          </button>
          <button v-if="!post.isNotice && !userStore.isBlocked" :class="$style.reportButton" @click="showReportModal">
            🚨 신고
          </button>
        </div>

        <!-- 광고 섹션 -->
        <AdSection :class="$style.boardAdSection" :adBanners="adBanners" @ad-click="goToAdLink" />

        <!-- 댓글 섹션 -->
        <CommentSection :boardId="boardId" :postWriterEmail="post?.writerEmail || ''" />

        <!-- 하단 액션 -->
        <div :class="$style.footer">
          <button :class="$style.actionButton" @click="goBack">목록으로</button>
        </div>
      </div>
    </div>

    <!-- 게시글 신고 모달 -->
    <div v-if="showReportModalFlag" :class="$style.reportModal">
      <div :class="$style.reportModalContent">
        <div :class="$style.reportModalHeader">
          <h3>신고하기</h3>
          <button @click="closeReportModal" :class="$style.closeButton">×</button>
        </div>
        <div :class="$style.reportModalBody">
          <p>신고 사유를 선택해주세요:</p>
          <div :class="$style.reportReasons">
            <label v-for="reason in reportReasons" :key="reason.value" :class="$style.reportReason">
              <input type="radio" v-model="selectedReportReason" :value="reason.value" :class="$style.reportRadio" />
              <span>{{ reason.label }}</span>
            </label>
          </div>
          <div :class="$style.reportTextarea">
            <textarea v-model="reportDescription" placeholder="추가 설명이 있다면 작성해주세요 (선택사항)" rows="3"></textarea>
          </div>
        </div>
        <div :class="$style.reportModalFooter">
          <button @click="closeReportModal" :class="$style.cancelButton">취소</button>
          <button @click="submitReport" :class="$style.submitButton" :disabled="!selectedReportReason || reportLoading">
            {{ reportLoading ? "신고 중..." : "신고하기" }}
          </button>
        </div>
      </div>
    </div>

    <!-- 이미지 확대 모달 -->
    <div v-if="showImageModal" :class="$style.imageModal" @click="closeImageModal">
      <div :class="$style.imageModalContent" @click.stop>
        <button @click="closeImageModal" :class="$style.closeImageButton">×</button>
        <img :src="selectedImageUrl" :class="$style.modalImage" alt="확대 이미지" />
      </div>
    </div>

    <!-- 공유 방법 선택 모달 -->
    <share-options-modal :visible="showShareModal" @close="showShareModal = false"
      @select="handleShareMethod"></share-options-modal>
  </div>
</template>

<script>
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { firestore, storage } from "/firebaseConfig";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { useUserStore } from "/src/store/user.js";
import CommentSection from "/src/components/CommentSection.vue";
import AdSection from "@/components/AdSection.vue";
import { createReport as createReportUtil } from "@/utils/write/report";
import { togglePostLike, deletePost as deletePostApi } from "@/utils/write/board";
import apiClient from "@/utils/api";
import { createNotification } from "@/utils/write/alarm";
import { incrementBannerClickCount } from "@/utils/write/banner";
import { setPageMeta, shareViaKakao, shareViaTwitter } from "@/utils/pageMeta";
import ShareOptionsModal from "@/components/modal/ShareOptionsModal.vue";
import { trackActivity, ACTIVITY_TYPES, trackShare } from "@/utils/activityTracker";
import { incrementVideoViewCount } from "@/utils/write/video";
import {
  cancelBandOffEntry,
  createBandOffEntry,
  getBandOffEntries,
  getBandOffSongs,
} from "@/utils/write/bandOff";
import { normalizeBandOffConfig } from "@/utils/bandOff/schema";

export default {
  name: "BoardDetail",
  components: {
    CommentSection,
    AdSection,
    ShareOptionsModal,
  },
  data() {
    return {
      post: null,
      loading: true,
      error: null,
      showShareModal: false,
      showReportModalFlag: false,
      selectedReportReason: "",
      reportDescription: "",
      reportLoading: false,
      reportReasons: [
        { value: "spam", label: "스팸/광고" },
        { value: "inappropriate", label: "부적절한 내용" },
        { value: "harassment", label: "괴롭힘/욕설" },
        { value: "fake", label: "허위 정보" },
        { value: "copyright", label: "저작권 침해" },
        { value: "other", label: "기타" },
      ],
      userStore: useUserStore(),
      showImageModal: false,
      selectedImageUrl: "",
      songRecommendVideoLoaded: false,
      adBanners: [], // 광고 배너 목록
      userHasLiked: false, // 사용자가 이미 좋아요를 눌렀는지 여부
      bandOffSongs: [],
      bandOffEntries: [],
      bandOffLoading: false,
      bandOffError: "",
      bandOffSubmitting: false,
    };
  },
  computed: {
    boardId() {
      return this.$route.params.boardId;
    },
    canEditPost() {
      if (!this.post || !this.userStore.isLogin) return false;
      // 관리자이거나 글쓴이인 경우
      return (
        this.userStore.auth === "admin" ||
        this.post.writer === this.userStore.nickname ||
        this.post.writer === this.userStore.email
      );
    },
    isBandOffPost() {
      return this.post?.type === "bandOff" || this.post?.category === "ggookoff";
    },
    bandOffConfig() {
      return normalizeBandOffConfig(this.post?.bandOffConfig || {});
    },
    songRecommendThumbUrl() {
      const info = this.post?.youtubeVideoInfo;
      if (!info) return "";
      if (info.videoId) return `https://img.youtube.com/vi/${info.videoId}/sddefault.jpg`;
      return info.videoThumbnail || "";
    },
    songRecommendVideoId() {
      const info = this.post?.youtubeVideoInfo;
      if (info?.videoId) return info.videoId;
      const url = this.post?.youtubeLink;
      if (!url || typeof url !== "string") return null;
      const u = url.trim();
      if (u.includes("youtu.be/")) return u.split("youtu.be/")[1].split(/[?&]/)[0];
      if (u.includes("youtube.com/watch?v=")) return u.split("v=")[1].split(/[?&]/)[0];
      return null;
    },
  },
  async mounted() {
    await Promise.all([this.loadPost(), this.fetchAdBanners()]);
    if (this.isBandOffPost) {
      await this.loadBandOffData();
    }
  },
  methods: {
    async loadPost() {
      try {
        this.loading = true;
        this.error = null;

        // 게시글 ID로 문서 조회
        const postDoc = await getDoc(doc(firestore, "boards", this.boardId));

        if (!postDoc.exists()) {
          this.error = "게시글을 찾을 수 없습니다.";
          return;
        }

        this.post = {
          id: postDoc.id,
          ...postDoc.data(),
        };

        const postImage = this.post.images?.length ? this.post.images[0] : undefined;
        setPageMeta({
          title: this.post.title,
          description: (this.post.content || this.post.title || "").slice(0, 200),
          image: typeof postImage === "string" && postImage.startsWith("http") ? postImage : undefined,
        });

        if (this.post.category === "ggookoff" && this.userStore.auth !== "admin") {
          this.error = "관리자만 접근할 수 있는 게시글입니다.";
          return;
        }

        // 비밀글 권한 체크
        if (this.post.isPrivate) {
          const isAdmin = this.userStore.auth === "admin";
          const isAuthor = this.userStore.email === this.post.writerEmail;

          if (!isAdmin && !isAuthor) {
            this.error = "이 게시글은 비밀글입니다. 작성자와 관리자만 확인할 수 있습니다.";
            return;
          }
        }

        // likedUsers 배열이 없으면 초기화
        if (!this.post.likedUsers) {
          this.post.likedUsers = [];
        }

        // 현재 사용자가 좋아요를 눌렀는지 확인
        if (this.userStore.isLogin && this.userStore.email) {
          this.userHasLiked = this.post.likedUsers.includes(this.userStore.email);
        }

        // 조회수 증가 (비동기로 처리)
        this.incrementViewCount();
      } catch (error) {
        console.error("게시글 로드 오류:", error);
        this.error = "게시글을 불러오는 중 오류가 발생했습니다.";
      } finally {
        this.loading = false;
      }
    },

    // 조회수 증가 메서드 (백엔드 API 호출)
    async incrementViewCount() {
      try {
        await apiClient.patch(`/api/board/${this.boardId}/view`);
        // 로컬 상태도 업데이트
        this.post.view = (this.post.view || 0) + 1;
      } catch (error) {
        console.warn("조회수 증가 실패:", error);
      }
    },

    async toggleLike() {
      if (!this.post) return;

      // 로그인 확인
      if (!this.userStore.isLogin) {
        this.userStore.showLoginPopup('recommend');
        return;
      }

      try {
        const userEmail = this.userStore.email;
        const isCurrentlyLiked = this.userHasLiked;

        // togglePostLike 함수 호출 (점수 부여 포함)
        const newLikedState = await togglePostLike(
          this.post.id,
          userEmail,
          isCurrentlyLiked
        );

        // 로컬 상태 업데이트
        if (newLikedState) {
          this.post.like += 1;
          this.userHasLiked = true;

          // 글쓴이에게 알림 전송
          if (this.post.writerEmail && this.post.writerEmail !== userEmail) {
            try {
              const postTitle = this.post.title?.length > 20
                ? this.post.title.substring(0, 20) + "..."
                : this.post.title;
              await createNotification({
                userId: this.post.writerEmail,
                type: "POST_LIKE",
                title: "게시글에 추천을 받았어요! 👍",
                message: `${this.userStore.nickname || '누군가'}님이 "${postTitle}" 글을 추천했습니다.`,
                targetType: "board",
                targetId: this.post.id,
                fromUserId: userEmail,
                fromUserNickname: this.userStore.nickname || null,
              });
            } catch (notifError) {
              console.warn("알림 전송 실패:", notifError);
            }
          }
        } else {
          this.post.like -= 1;
          this.userHasLiked = false;
        }
      } catch (error) {
        console.error("좋아요 오류:", error);
        this.$alert("좋아요 처리 중 오류가 발생했습니다.");
      }
    },

    showReportModal() {
      if (!this.userStore.isLogin) {
        this.userStore.showLoginPopup('report');
        return;
      }

      this.showReportModalFlag = true;
      this.selectedReportReason = "";
      this.reportDescription = "";
    },

    closeReportModal() {
      this.showReportModalFlag = false;
      this.selectedReportReason = "";
      this.reportDescription = "";
    },

    openImageModal(imageUrl) {
      this.selectedImageUrl = imageUrl;
      this.showImageModal = true;
    },

    closeImageModal() {
      this.showImageModal = false;
      this.selectedImageUrl = "";
    },
    loadSongRecommendVideo() {
      if (this.songRecommendVideoId) {
        this.songRecommendVideoLoaded = true;
        trackActivity(ACTIVITY_TYPES.VIDEO_PLAY, {
          target: "video",
          targetId: this.songRecommendVideoId,
          metadata: { source: "board_song_recommend" },
        }).catch(() => { });
        incrementVideoViewCount(this.songRecommendVideoId).catch(() => { });
      } else if (this.post?.youtubeLink) {
        window.open(this.post.youtubeLink, "_blank", "noopener,noreferrer");
      }
    },

    async loadBandOffData() {
      if (!this.isBandOffPost) return;
      try {
        this.bandOffLoading = true;
        this.bandOffError = "";
        const [songs, entries] = await Promise.all([
          getBandOffSongs(this.boardId),
          getBandOffEntries(this.boardId),
        ]);
        this.bandOffSongs = songs.filter((song) => song.active !== false);
        this.bandOffEntries = entries;
      } catch (error) {
        console.error("꾹오프 데이터 로드 오류:", error);
        this.bandOffError = "엔트리 현황을 불러오지 못했습니다.";
      } finally {
        this.bandOffLoading = false;
      }
    },
    getPartLimit(part) {
      return (this.bandOffConfig.partLimits && this.bandOffConfig.partLimits[part]) || 0;
    },
    getPartCount(songId, part) {
      return this.bandOffEntries.filter((entry) => entry.songId === songId && entry.part === part).length;
    },
    getMyEntry(songId) {
      if (!this.userStore.email) return null;
      return this.bandOffEntries.find((entry) => entry.songId === songId && entry.userId === this.userStore.email) || null;
    },
    canEnterPart(songId, part) {
      const partLimit = this.getPartLimit(part);
      if (partLimit > 0 && this.getPartCount(songId, part) >= partLimit) {
        return false;
      }
      if (this.getMyEntry(songId)) {
        return false;
      }
      if (this.bandOffConfig.maxParticipants > 0) {
        const activeUsers = new Set(this.bandOffEntries.map((entry) => entry.userId));
        if (!activeUsers.has(this.userStore.email) && activeUsers.size >= this.bandOffConfig.maxParticipants) {
          return false;
        }
      }
      if (this.bandOffConfig.recruitStatus !== "open") return false;
      return true;
    },
    async enterBandOff(song, part) {
      if (!this.userStore.isLogin) {
        this.userStore.showLoginPopup("communityRegister");
        return;
      }

      try {
        this.bandOffSubmitting = true;
        await createBandOffEntry({
          boardId: this.boardId,
          songId: song.id,
          songTitle: song.title,
          part,
          user: {
            email: this.userStore.email,
            nickname: this.userStore.nickname,
          },
        });
        await this.loadBandOffData();
        this.$alert("엔트리가 완료되었습니다.");
      } catch (error) {
        console.error("엔트리 실패:", error);
        this.$alert(error?.message || "엔트리 중 오류가 발생했습니다.");
      } finally {
        this.bandOffSubmitting = false;
      }
    },
    async cancelMyEntry(entryId) {
      if (!this.userStore.isLogin) return;
      const confirmed = await this.$confirm("내 엔트리를 취소할까요?");
      if (!confirmed) return;
      try {
        this.bandOffSubmitting = true;
        await cancelBandOffEntry({
          boardId: this.boardId,
          entryId,
          userEmail: this.userStore.email,
          isAdmin: this.userStore.auth === "admin",
        });
        await this.loadBandOffData();
        this.$alert("엔트리를 취소했습니다.");
      } catch (error) {
        console.error("엔트리 취소 실패:", error);
        this.$alert(error?.message || "엔트리 취소 중 오류가 발생했습니다.");
      } finally {
        this.bandOffSubmitting = false;
      }
    },

    async submitReport() {
      if (!this.selectedReportReason) {
        this.$alert("신고 사유를 선택해주세요.");
        return;
      }

      try {
        this.reportLoading = true;
        const documentPath = `boards/${this.boardId}`;
        await createReportUtil({
          reporter: this.userStore.email,
          reporterNickname: this.userStore.nickname,
          documentPath,
          documentType: "post",
          reason: this.selectedReportReason,
          description: this.reportDescription.trim(),
        });

        this.$alert("신고가 접수되었습니다.");
        this.closeReportModal();
      } catch (error) {
        console.error("신고 제출 오류:", error);
        this.$alert("신고 제출 중 오류가 발생했습니다.");
      } finally {
        this.reportLoading = false;
      }
    },

    goBack() {
      this.$router.push("/board");
    },

    async handleShareMethod(method) {
      this.showShareModal = false;
      const url = window.location.href;
      const title = this.post?.title || '게시글';

      if (method === 'kakao') {
        await shareViaKakao();
        trackShare("board", this.post?.id, "kakao");
        return;
      }
      if (method === 'twitter') {
        shareViaTwitter();
        trackShare("board", this.post?.id, "twitter");
        return;
      }
      if (method === 'copy') {
        trackShare("board", this.post?.id, "copy");
        this.copyToClipboard(url);
        return;
      }
      if (method === 'native' && navigator.share) {
        try {
          await navigator.share({ title, text: `${title} - 링꾹라이브`, url });
          trackShare("board", this.post?.id, "native");
        } catch (error) {
          if (error.name !== 'AbortError') {
            trackShare("board", this.post?.id, "copy_fallback");
            this.copyToClipboard(url);
          }
        }
      }
    },

    copyToClipboard(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$alert('링크가 클립보드에 복사되었습니다.');
        }).catch(() => {
          this.fallbackCopyToClipboard(text);
        });
      } else {
        this.fallbackCopyToClipboard(text);
      }
    },

    fallbackCopyToClipboard(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        this.$alert('링크가 클립보드에 복사되었습니다.');
      } catch (err) {
        this.$alert('링크 복사에 실패했습니다.');
      }
      document.body.removeChild(textArea);
    },

    getCategoryName(category) {
      if (!category) return ""; // 카테고리가 없는 경우 빈 문자열 반환 (공지사항)
      const categoryNames = {
        notice: "📢 공지사항",
        performance: "🎭 공연후기",
        recruit: "💼 구인구직",
        recommend: "⭐ 추천게시판",
        songRecommend: "🎵 곡추천",
        ggookoff: "🎪 꾹오프",
        free: "💬 자유게시판",
        update: "🔄 업데이트",
        permission: "🔑 권한신청",
        liveRegister: "🎭 공연정보등록신청",
        suggestion: "💡 건의사항",
        bugreport: "🐛 버그리포트",
      };
      return categoryNames[category] || "게시판";
    },

    formatDate(timestamp) {
      if (!timestamp) return "날짜 정보 없음";

      try {
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString("ko-KR", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        return "날짜 정보 없음";
      }
    },

    formatContentWithLinks(content) {
      if (!content) return "";

      // URL 패턴 매칭 (http, https, www로 시작하는 링크)
      const urlPattern =
        /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/g;

      return content.replace(urlPattern, (url) => {
        // www로 시작하는 경우 http:// 추가
        let href = url;
        if (url.startsWith("www.")) {
          href = "http://" + url;
        } else if (!url.startsWith("http://") && !url.startsWith("https://")) {
          href = "https://" + url;
        }

        return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: #66bdff; text-decoration: underline; word-break: break-all;">${url}</a>`;
      });
    },

    editPost() {
      // 수정 페이지로 이동 (쿼리 파라미터로 수정 모드 전달)
      this.$router.push({
        path: "/board/write",
        query: { editId: this.boardId },
      });
    },

    async deletePost() {
      const confirmed = await this.$confirm("정말로 이 게시글을 삭제하시겠습니까?");
      if (!confirmed) return;

      try {
        await deletePostApi(this.boardId);
        this.$alert("게시글이 삭제되었습니다.");
        this.$router.push("/board");
      } catch (error) {
        console.error("게시글 삭제 오류:", error);
        this.$alert("게시글 삭제 중 오류가 발생했습니다.");
      }
    },

    async fetchAdBanners() {
      try {
        // banners 컬렉션에서 active가 true이고 type이 "detail"인 데이터 가져오기
        const bannersQuery = query(
          collection(firestore, "banners"),
          where("active", "==", true),
          where("type", "==", "detail")
        );
        const bannersSnapshot = await getDocs(bannersQuery);

        const customBannerData = await Promise.all(
          bannersSnapshot.docs.map(async (doc) => {
            const data = doc.data();

            // 광고 기간 체크 (startDateTime과 endDateTime이 있는 경우)
            if (data.startDateTime || data.endDateTime) {
              const now = new Date();

              // 시작일시 체크
              if (data.startDateTime) {
                const startDate = new Date(data.startDateTime);
                if (now < startDate) {
                  // 아직 광고 시작 전
                  return null;
                }
              }

              // 종료일시 체크
              if (data.endDateTime) {
                const endDate = new Date(data.endDateTime);
                if (now > endDate) {
                  // 광고 기간 종료됨
                  return null;
                }
              }
            }

            // 배너 이미지 URL 처리
            let bannerImgUrl = data.img;
            if (!bannerImgUrl) {
              console.error("배너 이미지가 없습니다:", data.title);
              return null;
            }

            if (!bannerImgUrl.startsWith("http")) {
              try {
                const imageRef = storageRef(storage, `banner/${data.img}`);
                bannerImgUrl = await getDownloadURL(imageRef);
              } catch (e) {
                console.error("배너 이미지 로드 실패:", e);
                return null;
              }
            }

            return {
              id: doc.id,
              source: "custom", // 출처 표시
              title: data.title,
              img: bannerImgUrl,
              link: data.link || "",
              date: data.date || "",
              place: data.place || "",
              type: "site", // 커스텀 배너는 site 타입으로
              priority: data.priority || 0, // 우선순위
            };
          })
        );

        const allBanners = customBannerData.filter(Boolean);

        // 우선순위로 정렬 (priority가 높을수록 먼저 표시)
        allBanners.sort((a, b) => {
          const priorityA = a.priority || 0;
          const priorityB = b.priority || 0;
          return priorityB - priorityA;
        });

        this.adBanners = allBanners;
        // console.log("배너 데이터 개수:", this.adBanners.length);
      } catch (error) {
        console.error("광고 배너 가져오기 실패:", error);
        this.adBanners = [];
      }
    },

    async goToAdLink(ad) {
      // 클릭 횟수 증가
      await this.incrementAdClickCount(ad);

      // 커스텀 배너(source: "custom")이고 링크가 있는 경우 외부 링크로 이동
      if (ad.source === "custom" && ad.link) {
        window.open(ad.link, "_blank");
      } else if (ad.source === "live") {
        // 라이브 배너인 경우 해당 라이브 상세 페이지로 이동
        this.$router.push({
          name: "LiveDetail",
          params: { slug: ad.slug || ad.id },
        });
      }
    },

    async incrementAdClickCount(ad) {
      try {
        await incrementBannerClickCount(ad);
      } catch (error) {
        console.error("광고 클릭 횟수 증가 오류:", error);
        // 클릭 횟수 증가 실패해도 사용자 경험에는 영향을 주지 않음
      }
    },
  },
};
</script>

<style module>
/* Medium: 481px ~ (기본 스타일, 최대 1024px) */
.container {
  width: 100%;
  margin: auto;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.paper {
  width: 100%;
  background: white;
  min-height: calc(100vh - 90px);
  display: flex;
  flex-direction: column;
}

/* 로딩 상태 */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 20px;
  min-height: 350px;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #66bdff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* 에러 상태 */
.error {
  text-align: center;
  padding: 50px 20px;
  color: #dc3545;
}

.error p {
  margin: 0 0 20px 0;
  font-size: 1rem;
}

/* 모바일 전용 헤더 */
.detailHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 18px;
  margin-bottom: 0;
}

.backArrowBtn {
  background: transparent;
  border: none;
  font-size: 22px;
  cursor: pointer;
  padding: 6px 10px;
  color: #333;
  transition: opacity 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.backArrowBtn:hover {
  opacity: 0.7;
}

.shareBtn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
}

.shareBtn:hover {
  opacity: 0.7;
}

.shareIcon {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

/* 헤더 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 1px solid #e9ecef;
  background: #fafafa;
  border-radius: 8px 8px 0 0;
}

.backButton {
  background: #fff;
  color: #222;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: all 0.2s ease;
}

.backButton:hover {
  background: #f8f9fa;
  border-color: #999;
}

.headerActions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.editButton {
  background: #66bdff;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.editButton:hover {
  background: #4a9eff;
  transform: translateY(-1px);
}

.deleteButton {
  background: #ff6b6b;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.deleteButton:hover {
  background: #ff5252;
  transform: translateY(-1px);
}

.likeButton {
  background: #fff;
  color: #333;
  border: 1px solid #ddd;
  padding: 12px 32px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.likeButton:hover {
  background: #ff5582;
  color: white;
  border-color: #ff5582;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(255, 107, 157, 0.4);
}

.likeButton:active {
  transform: translateY(0);
}

.likeButton.liked {
  background: #66bdff;
  color: white;
  border-color: #66bdff;
  box-shadow: 0 4px 12px rgba(102, 189, 255, 0.3);
}

.likeButton.liked:hover {
  background: #4a9eff;
  border-color: #4a9eff;
  box-shadow: 0 6px 16px rgba(102, 189, 255, 0.4);
}

.reportButton {
  background: #999;
  color: white;
  border: none;
  padding: 12px 32px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "NanumSquareRoundB", sans-serif;
  box-shadow: 0 4px 12px rgba(153, 153, 153, 0.3);
}

.reportButton:hover {
  background: #777;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(153, 153, 153, 0.4);
}

.reportButton:active {
  transform: translateY(0);
}

/* 게시글 정보 (헤더 영역) */
.postInfo {
  padding: 25px;
  padding-top: 0px;
  border-bottom: 1px solid #f0f0f0;
}

.categoryRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  flex-wrap: wrap;
  gap: 10px;
}

.categoryLeft {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.categoryRight {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.noticeBadge {
  display: inline-block;
  background: #f0ad4e;
  color: white;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  font-family: "NanumSquareRoundB", sans-serif;
  box-shadow: 0 2px 4px rgba(240, 173, 78, 0.3);
}

.category {
  display: inline-block;
  background: #e3f2fd;
  color: #1976d2;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
}

.title {
  margin: 0 0 12px 0;
  font-size: 22px;
  font-weight: 700;
  color: #333;
  line-height: 1.4;
  font-family: "NanumSquareRoundB", sans-serif;
}

.meta {
  display: flex;
  gap: 15px;
  font-size: 13px;
  color: #666;
}

.writerInfo {
  display: flex;
  align-items: center;
  gap: 4px;
}

.adminEmoji {
  font-size: 13px;
  line-height: 1;
  flex-shrink: 0;
}

.writer {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.date {
  font-size: 13px;
  color: #999;
}

.metaRight {
  display: flex;
  align-items: center;
  gap: 12px;
}

.viewCount {
  font-size: 13px;
  color: #999;
}

/* 곡추천 게시글: 유튜브 영상 블록 (VideoList VideoCard 스타일) */
.songRecommendVideoBlock {
  margin: 10px;
}

.songRecommendVideoCard {
  display: flex;
  align-items: flex-start;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 16px;
  border: 1px solid rgba(139, 107, 184, 0.08);
  box-shadow: 0 2px 12px rgba(45, 27, 78, 0.04);
  transition: all 0.25s ease;
}

.songRecommendVideoCard:hover {
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 6px 24px rgba(45, 27, 78, 0.1);
}

.videoThumbnailWrapper {
  flex-shrink: 0;
  width: 240px;
  padding: 10px;
  cursor: pointer;
}

.videoContainer {
  width: 100%;
  position: relative;
  height: 0;
  padding-bottom: 56.25%;
  border-radius: 14px;
  overflow: hidden;
  /* background: #000; */
  box-shadow: 0 4px 16px rgba(45, 27, 78, 0.1);
}

.videoEmbedIframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.thumbnailContainer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  /* background-color: #000; */
}

.videoThumbnail {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.2s;
}

.thumbPlaceholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: rgba(255, 255, 255, 0.6);
}

.thumbnailContainer:hover .videoThumbnail {
  opacity: 0.7;
}

.playButton {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 48px;
  opacity: 0.8;
  transition: opacity 0.2s, transform 0.2s;
  pointer-events: none;
}

.thumbnailContainer:hover .playButton {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1.2);
}

.videoInfo {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  min-width: 0;
}

.videoHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.videoAuthor {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.authorAvatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #b8a9d4 0%, #e8b4d4 50%, #a8d4f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1rem;
  flex-shrink: 0;
  font-family: "NanumSquareRoundB", sans-serif;
  box-shadow: 0 2px 8px rgba(139, 107, 184, 0.2);
}

.authorAvatarImg {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.authorInfo {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.authorName {
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: bold;
  font-size: 0.9375rem;
  color: #2d1b4e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.videoTitleText {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 0.875rem;
  color: #2d1b4e;
  line-height: 1.5;
  padding: 5px;
  word-wrap: break-word;
}

.videoAuthorLink,
.videoTitleLink {
  text-decoration: none;
  color: inherit;
  display: block;
  min-width: 0;
  cursor: pointer;
}

.videoAuthorLink:hover .authorName,
.videoTitleLink:hover .videoTitleText {
  color: #8b6bb8;
}

.songRecommendVideoLinkSimple {
  display: block;
  padding: 14px 20px;
  font-size: 14px;
  color: #66bdff;
  text-decoration: none;
  font-family: "NanumSquareRoundR", sans-serif;
}

.songRecommendVideoLinkSimple:hover {
  text-decoration: underline;
}

/* 게시글 내용 */
.postContent {
  padding: 25px;
}

.contentText {
  font-size: 15px;
  line-height: 1.8;
  color: #333;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: "NanumSquareRoundR", sans-serif;
}

.contentText a {
  color: #66bdff;
  text-decoration: underline;
  word-break: break-all;
  transition: color 0.2s ease;
}

.contentText a:hover {
  color: #4a9eff;
}

/* 트위터 스타일 이미지 그리드 */
.postImages {
  margin-top: 20px;
  border-radius: 12px;
  overflow: hidden;
  display: grid;
  gap: 2px;
  max-width: 100%;
}

/* 1개 이미지 */
.imageGrid1 {
  grid-template-columns: 1fr;
}

/* 2개 이미지 - 위아래로 배치 */
.imageGrid2 {
  grid-template-columns: 1fr;
}

.postImageWrapper {
  position: relative;
  width: 100%;
  background: #f0f0f0;
  cursor: pointer;
  overflow: hidden;
}

.postImage {
  width: 100%;
  height: auto;
  object-fit: contain;
  transition: opacity 0.2s ease;
}

.postImageWrapper:hover .postImage {
  opacity: 0.9;
}

/* 이미지 확대 모달 */
.imageModal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.imageModalContent {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modalImage {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
}

.closeImageButton {
  position: absolute;
  top: -40px;
  right: 0;
  background: transparent;
  color: white;
  border: none;
  font-size: 36px;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.closeImageButton:hover {
  color: #66bdff;
}

.bandOffSection {
  margin: 0 25px 20px;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background: #f8fbff;
  padding: 14px;
}

.bandOffHeader {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}

.bandOffHeader h3 {
  margin: 0;
  font-size: 16px;
}

.bandOffHeader p {
  margin: 0;
  font-size: 12px;
  color: #475569;
}

.bandOffLoading {
  font-size: 13px;
  color: #64748b;
}

.bandOffError {
  margin: 0 0 10px 0;
  color: #b91c1c;
  font-size: 12px;
}

.bandOffSongList {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bandOffSongItem {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
}

.bandOffSongTitle {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
}

.bandOffPartList {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.bandOffPartButton {
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #fff;
  color: #334155;
  font-size: 11px;
  padding: 4px 10px;
}

.bandOffPartButton.bandOffDisabled {
  opacity: 0.45;
}

.bandOffMyEntry {
  margin-top: 8px;
  font-size: 12px;
  color: #1d4ed8;
  display: flex;
  align-items: center;
  gap: 8px;
}

.bandOffCancelButton {
  border: none;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 11px;
  background: #ef4444;
  color: #fff;
}

/* 좋아요 섹션 */
.likeSection {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 25px;
  border-bottom: 1px solid #f0f0f0;
}

/* 하단 액션 */
.footer {
  padding: 18px 25px;
  border-top: 1px solid #f0f0f0;
  border-radius: 0 0 8px 8px;
  display: flex;
  justify-content: center;
  padding-bottom: 80px;
}

.actionButton {
  background: #66bdff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.actionButton:hover {
  background: #4a9eff;
  transform: translateY(-1px);
}

/* 신고 모달 */
.reportModal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.reportModalContent {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 450px;
  max-height: 80vh;
  overflow-y: auto;
}

.reportModalHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px;
  border-bottom: 1px solid #e9ecef;
}

.reportModalHeader h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.closeButton {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.closeButton:hover {
  color: #333;
}

.reportModalBody {
  padding: 18px;
}

.reportModalBody p {
  margin: 0 0 15px 0;
  font-size: 14px;
  color: #666;
}

.reportReasons {
  margin-bottom: 20px;
}

.reportReason {
  display: block;
  margin-bottom: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
}

.reportRadio {
  margin-right: 8px;
}

.reportTextarea {
  margin-top: 15px;
}

.reportTextarea textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  resize: vertical;
}

.reportTextarea textarea:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 2px rgba(102, 189, 255, 0.2);
}

.reportModalFooter {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 18px;
  border-top: 1px solid #e9ecef;
}

.cancelButton {
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.cancelButton:hover {
  background: #5a6268;
}

.submitButton {
  background: #dc3545;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.submitButton:hover:not(:disabled) {
  background: #c82333;
}

.submitButton:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.boardAdSection {
  margin: 20px;
}

/* ========================================
   Small (~480px)
   ======================================== */
@media screen and (max-width: 480px) {
  .container {
    width: 100%;
  }

  .paper {
    min-height: calc(100vh - 90px);
  }

  .detailHeader {
    padding: 8px 15px;
  }

  .backArrowBtn {
    font-size: 20px;
    padding: 4px 8px;
  }

  .shareIcon {
    width: 20px;
    height: 20px;
  }

  .postInfo {
    padding: 15px;
    padding-top: 0px;
  }

  .categoryRow {
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
  }

  .categoryLeft {
    gap: 6px;
  }

  .categoryRight {
    gap: 6px;
  }

  .category {
    font-size: 10px;
    padding: 3px 8px;
  }

  .noticeBadge {
    font-size: 10px;
    padding: 4px 10px;
  }

  .backButton,
  .editButton,
  .deleteButton {
    font-size: 10px;
    padding: 4px 8px;
    border-radius: 6px;
  }

  .title {
    font-size: 18px;
    margin-bottom: 10px;
    line-height: 1.4;
  }

  .meta {
    flex-direction: column;
    gap: 6px;
    font-size: 12px;
  }

  .writer {
    font-size: 12px;
  }

  .date {
    font-size: 11px;
  }

  .viewCount {
    font-size: 11px;
  }

  .metaRight {
    gap: 8px;
  }

  .postContent {
    padding: 15px;
  }

  .contentText {
    font-size: 14px;
    line-height: 1.6;
  }

  .likeSection {
    padding: 15px;
    gap: 8px;
    flex-wrap: wrap;
  }

  .likeButton,
  .reportButton {
    padding: 10px 20px;
    font-size: 14px;
  }

  .footer {
    padding: 12px 15px;
    padding-bottom: 80px;
  }

  .actionButton {
    padding: 10px 20px;
    font-size: 13px;
  }

  .reportModalContent {
    width: 100%;
    height: 100%;
    border-radius: 0;
    max-width: 100%;
    max-height: 100%;
  }

  .reportModalHeader,
  .reportModalBody,
  .reportModalFooter {
    padding: 12px;
  }

  .reportModalHeader h3 {
    font-size: 16px;
  }

  .reportModalBody p {
    font-size: 13px;
    margin-bottom: 12px;
  }

  .reportReason {
    font-size: 13px;
    margin-bottom: 8px;
  }

  .reportTextarea textarea {
    font-size: 13px;
    padding: 8px;
  }

  .cancelButton,
  .submitButton {
    padding: 8px 16px;
    font-size: 13px;
  }

  .loading {
    padding: 40px 15px;
    min-height: 300px;
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #66bdff;
    margin-bottom: 15px;
  }

  .loading p {
    font-size: 14px;
  }

  .error {
    padding: 40px 15px;
  }

  .error p {
    font-size: 0.95rem;
    margin-bottom: 15px;
  }

  .imageModalContent {
    max-width: 95vw;
    max-height: 95vh;
  }

  .closeImageButton {
    top: -35px;
    right: -5px;
    font-size: 32px;
    width: 36px;
    height: 36px;
  }

  .modalImage {
    max-height: 85vh;
  }

  /* 곡추천 카드: VideoList와 동일 반응형 */
  .songRecommendVideoCard {
    flex-direction: column;
  }

  .videoThumbnailWrapper {
    width: 100%;
  }

  .videoHeader {
    flex-wrap: wrap;
    gap: 8px;
  }

  .authorAvatar,
  .authorAvatarImg {
    width: 32px;
    height: 32px;
  }

  .authorAvatar {
    font-size: 0.875rem;
  }

  .authorName {
    font-size: 0.8125rem;
  }

  .videoTitleText {
    font-size: 0.8125rem;
    line-height: 1.4;
  }
}
</style>
